import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import api from '../../services/api';
import HeldTable, { ScopeSelect } from './HeldTable';
import { baseLineOptions, shortDate } from './charts';
import { NAVY, MAUVE, SLATE } from './palette';
import { errorText, longDate } from './format';

// "Our manager selection": every manager Drummond holds now, its percentile in the sub
// asset class today, and its return and percentile since we started holding it.
export default function HeldManagersTab({ catalogue, scoring, asAt }) {
  const [scope, setScope] = useState('models');
  const [scopes, setScopes] = useState([]);
  const [includeExited, setIncludeExited] = useState(false);
  const [statistic, setStatistic] = useState('excess_return');
  const [basis, setBasis] = useState('sac');          // percentile peer group: sac | style | tilt
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [series, setSeries] = useState(null);
  const [seriesFor, setSeriesFor] = useState(null);

  useEffect(() => {
    api.get('/api/quant-screener/held/scopes').then((r) => setScopes(r.data?.scopes || [])).catch((e) => setErr(errorText(e)));
  }, []);

  const run = async () => {
    setBusy(true); setErr(null); setSeries(null); setSeriesFor(null);
    try {
      const r = await api.post('/api/quant-screener/held', { scope, as_at: asAt || null, statistic, include_exited: includeExited, ...scoring });
      setData(r.data);
    } catch (e) { setErr(errorText(e)); } finally { setBusy(false); }
  };
  useEffect(() => { run(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const showSeries = async (tr) => {
    setSeriesFor(tr); setSeries(null);
    try {
      const r = await api.get('/api/quant-screener/held/series', { params: { tr_code: tr, scope, as_at: asAt || null, include_exited: includeExited } });
      setSeries(r.data);
    } catch (e) { setErr(errorText(e)); }
  };

  const scorable = (catalogue?.statistics || []).filter((s) => s.direction !== 0);

  return (
    <div className="qs-held">
      <div className="qs-block">
        <div className="qs-metric-form">
          <label className="qs-field">Since-held scope
            <ScopeSelect scopes={scopes} scope={scope} setScope={setScope} />
          </label>
          <label className="qs-field">Current percentiles on
            <select value={statistic} onChange={(e) => setStatistic(e.target.value)}>
              {scorable.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
          <label className="qs-field" title="Off = only managers held now. On = also managers that were held in scope and have since been sold, measured over their holding period">
            <input type="checkbox" checked={includeExited} onChange={(e) => setIncludeExited(e.target.checked)} /> include exited managers
          </label>
          <label className="qs-field" title="Peer group for every percentile on this tab. Style and Tilt rank the fund only against funds of the same style or tilt inside its sub asset class, which strips out some of the beta a broad cohort carries. Changes apply instantly.">
            Percentile basis
            <select value={basis} onChange={(e) => setBasis(e.target.value)}>
              <option value="sac">sub asset class</option>
              <option value="style">style within sub asset class</option>
              <option value="tilt">tilt within sub asset class</option>
            </select>
          </label>
          <button type="button" className="qs-btn qs-btn-primary" onClick={run} disabled={busy}>{busy ? 'Running…' : 'Refresh'}</button>
          {data && (
            <span className="qs-muted" title={(data.scope_codes || []).join(', ')}>
              scope: <b>{data.scope_label || data.scope}</b> ({(data.scope_codes || []).length} portfolios) · as at {longDate(data.as_at)} · holdings history {longDate(data.holdings_start)} to {longDate(data.holdings_end)}{data.weighting ? ` · score: ${data.weighting}` : ''}
            </span>
          )}
        </div>
      </div>
      {err && <div className="qs-alert">{err}</div>}
      <HeldTable data={data} basis={basis} seriesFor={seriesFor} onShowSeries={showSeries} />
      {data && seriesFor && (
        <div className="qs-tile qs-tile-wide" style={{ marginTop: 12 }}>
          <div className="qs-tile-title">
            {series ? `${series.name || series.tr_code} since held (${series.censored ? 'at least ' : ''}${series.since_held}${series.exited ? ` to exit ${series.exited_on}` : ''})` : 'Loading…'}
            {series && <span className="qs-muted"> cumulative return; cohort median across {series.cohort_n} {series.sub_asset_class} funds with full coverage of the window</span>}
          </div>
          {series && (
            <div style={{ height: 320 }}>
              <Line
                data={{ labels: series.dates.map(shortDate), datasets: [
                  { label: series.name || series.tr_code, data: series.fund, borderColor: NAVY, backgroundColor: NAVY, borderWidth: 2 },
                  { label: 'benchmark', data: series.benchmark, borderColor: MAUVE, backgroundColor: MAUVE },
                  { label: 'cohort median', data: series.cohort_median, borderColor: SLATE, backgroundColor: SLATE, borderDash: [4, 3] },
                ] }}
                options={baseLineOptions('pct')}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
